import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import Slider from 'react-slick';
import ReactPlayer from 'react-player/youtube';
import PropTypes from 'prop-types';
import { VideoCameraFilled } from '@ant-design/icons';
import { device } from 'utils';
import ArrowRightIcon from './Icons/ArrowRight';
import ArrowLeftIcon from './Icons/ArrowLeftIcon';
import { useIsMobile } from '../helpers/useIsMobile';
import ArrowLeftMobileIcon from './Icons/ArrowLeftMobileIcon';
import ArrowRightMobile from './Icons/ArrowRightMobile';

const MAX_VISIBLE_THUMBS = 4;

const isYoutubeUrl = (url = '') =>
  url?.includes('youtube.com') || url?.includes('youtu.be');

const getYoutubeId = (url = '') => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

  const match = url.match(regExp);

  return match && match[2].length === 11 ? match[2] : null;
};

const getYoutubeThumbnail = (url = '') => {
  const videoId = getYoutubeId(url);
  if (!videoId) return null;

  return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
};

const ProjectImages = ({ projectImages = [] }) => {
  const useDefaultDots = useIsMobile();
  const [currentSlide, setCurrentSlide] = useState(0);

  const safeProjectImages = useMemo(() => {
    return Array.isArray(projectImages) ? projectImages.filter(Boolean) : [];
  }, [projectImages]);

  const getVisibleThumbRange = () => {
    if (safeProjectImages.length <= MAX_VISIBLE_THUMBS) {
      return {
        start: 0,
        end: safeProjectImages.length,
      };
    }

    let start = currentSlide - 1;

    if (start < 0) {
      start = 0;
    }

    let end = start + MAX_VISIBLE_THUMBS;

    if (end > safeProjectImages.length) {
      end = safeProjectImages.length;
      start = end - MAX_VISIBLE_THUMBS;
    }

    return { start, end };
  };

  const { start, end } = getVisibleThumbRange();

  const renderCustomPaging = (i) => {
    const item = safeProjectImages[i];
    if (!item) return <HiddenThumb />;

    const isVisible = i >= start && i < end;

    if (!isVisible) {
      return <HiddenThumb />;
    }

    return (
      <ThumbAnchor>
        {isYoutubeUrl(item) ? (
          <VideoThumbBox>
            {getYoutubeThumbnail(item) ? (
              <DotImage src={getYoutubeThumbnail(item)} alt="video-thumb" />
            ) : (
              <VideoFallbackIcon>
                <VideoCameraFilled />
              </VideoFallbackIcon>
            )}

            <VideoOverlay>
              <VideoCameraFilled />
            </VideoOverlay>

            <VideoBadge>Video</VideoBadge>
          </VideoThumbBox>
        ) : (
          <DotImage src={item} alt="project-thumb" loading="lazy" />
        )}
      </ThumbAnchor>
    );
  };

  const settings = {
    dots: true,
    infinite: safeProjectImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: safeProjectImages.length > 1,
    fade: false,
    cssEase: 'ease',
    adaptiveHeight: false,
    waitForAnimate: true,
    lazyLoad: 'ondemand',
    afterChange: (index) => setCurrentSlide(index),
    nextArrow: (
      <div>{useDefaultDots ? <ArrowRightMobile /> : <ArrowRightIcon />}</div>
    ),
    prevArrow: (
      <div>{useDefaultDots ? <ArrowLeftMobileIcon /> : <ArrowLeftIcon />}</div>
    ),
  };

  if (!useDefaultDots) {
    settings.customPaging = renderCustomPaging;
  }

  return (
    <Wrapper $useDefaultDots={useDefaultDots}>
      <div className={safeProjectImages.length > 1 ? "images" : "noImage"}>
        <Slider {...settings}>
          {safeProjectImages.map((item, index) => (
            <div key={`${item}-${index}`} className="img">
              {isYoutubeUrl(item) ? (
                <YouTubeWrapper>
                  <ReactPlayer
                    url={item}
                    width="100%"
                    height="100%"
                    controls
                    // light={getYoutubeThumbnail(item)}
                    playing={false}
                    className="react-player"
                  />
                </YouTubeWrapper>
              ) : (
                <ImgBox>
                  <img
                    className="blur-bg"
                    src={item}
                    alt="project-img-bg"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                  <div className="blur-overlay" />
                  <img
                    className="main-image"
                    src={item}
                    alt="project-img"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                </ImgBox>
              )}
            </div>
          ))}
        </Slider>
      </div>
    </Wrapper>
  );
};

ProjectImages.propTypes = {
  projectImages: PropTypes.array,
};

export default ProjectImages;

const Wrapper = styled.div`
  .noImage {
    margin-bottom: -10px;
  }

  .images {
    margin-bottom: 25px;
  }

  .images div:focus {
    outline: none;
  }

  .slick-prev:before,
  .slick-next:before {
    content: '';
    font-size: 0;
  }

  .slick-list {
    background: transparent;
    border-top-right-radius: 24px;
    border-bottom-left-radius: 24px;
    overflow: hidden;
  }

  .slick-slide {
    visibility: visible !important;
  }

  .slick-slide > div {
    height: 100%;
  }

  .react-player,
  .react-player iframe {
    background: transparent;
  }

  .slick-dots {
    bottom: -96px;
    display: flex !important;
    justify-content: center;
    align-items: center;
    gap: 8px;
    height: 85px;
    margin: 0;
    padding: 0;
    width: 100%;

    li {
      width: auto;
      height: 85px;
      margin: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    li button {
      width: auto;
      height: 85px;
      padding: 0;
      margin: 0;
      display: block;
    }

    li button:before {
      display: none;
    }

    @media ${device.allMobile} {
      bottom: -40px;
      height: auto;
      gap: 4px;

      li {
        width: auto;
        height: auto;
        display: inline-block;
      }

      li button {
        width: 20px;
        height: 20px;
      }

      li button:before {
        display: block;
      }
    }
  }

  ${({ $useDefaultDots }) =>
    !$useDefaultDots &&
    `
      .slick-dots li {
        display: inline-flex;
      }

      .slick-dots li:has(span[data-hidden-thumb="true"]) {
        display: none;
      }
    `}

  ${({ $useDefaultDots }) =>
    $useDefaultDots &&
    `
      .slick-dots li {
        z-index: 2;
        display: inline-block;
      }

      .slick-dots li button:before {
        font-size: 10px;
      }
    `}

  .slick-dots li.slick-active button:before {
    color: #9dbc60;
  }

  .slick-next {
    z-index: 2;
    position: absolute;
    width: 30px;
    top: unset;
    right: 50px;
    bottom: ${({ $useDefaultDots }) => ($useDefaultDots ? '-70px' : '-87px')};
    height: 30px;
  }

  .slick-prev {
    left: 50px;
    z-index: 2;
    top: unset;
    bottom: ${({ $useDefaultDots }) => ($useDefaultDots ? '-70px' : '-87px')};
    width: 30px;
    height: 30px;
  }

  @media (max-width: 1440px) {
    .slick-next {
      right: 20px;
    }
    .slick-prev {
      left: 15px;
    }
  }

  @media (max-width:1137px) {
    .slick-next {
      right: -5px;
    }
    .slick-prev {
      left: -5px;
    }
  }

  .slick-track > div {
    padding: 0 5px;
  }

  .img {
    border-top-right-radius: 24px;
    border-bottom-left-radius: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
  }
`;

const HiddenThumb = () => (
  <span data-hidden-thumb="true" style={{ display: 'none' }} />
);

const ThumbAnchor = styled.a`
  display: block;
`;

const DotImage = styled.img`
  width: 128px;
  height: 85px;
  object-fit: cover;
  border-radius: 8px;
  display: block;
`;

const VideoThumbBox = styled.div`
  width: 128px;
  height: 85px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  display: block;
  background: transparent;
  border: 1px solid #e4e7ec;
`;

const VideoFallbackIcon = styled.div`
  width: 128px;
  height: 85px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #667085;
  background: linear-gradient(180deg, #f8fafc 0%, #eef2f6 100%);
  font-size: 28px;
`;

const VideoOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.24);
  color: #ffffff;
  font-size: 24px;
`;

const VideoBadge = styled.span`
  position: absolute;
  bottom: 6px;
  right: 8px;
  font-size: 11px;
  line-height: 1;
  color: #667085;
  background: rgba(255, 255, 255, 0.88);
  padding: 3px 6px;
  border-radius: 999px;
`;

const mediaBoxStyles = `
  width: 100%;
  height: clamp(360px, 36vw, 520px);
  overflow: hidden;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  background: transparent;

  @media (max-width: 1180px) {
    height: clamp(360px, 54vw, 520px);
  }

  @media (max-width: 990px) {
    aspect-ratio: 1;
    height: auto;
    min-height: 320px;
  }

  @media (max-width: 500px) {
    max-height: 256px;
    min-height: 256px;
  }

  @media (min-width: 500px) and (max-width: 768px) {
    height: 356px;
  }
`;

const YouTubeWrapper = styled.div`
  ${mediaBoxStyles}

  .react-player {
    position: absolute;
    inset: 0;
  }

  iframe {
    width: 100% !important;
    height: 100% !important;
  }
`;

const ImgBox = styled.div`
  ${mediaBoxStyles}

  .blur-bg {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    filter: blur(8px);
    transform: scale(1.08);
    opacity: 0.9;
  }

  .blur-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.18);
  }

  .main-image {
    position: relative;
    z-index: 2;
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
  }
`;
